import React, { useMemo, useState } from 'react'
import { CBadge, CCard, CCardBody, CCardHeader, CSmartTable } from '@coreui/react-pro'
import TableSearchToolbar from 'src/components/common/TableSearchToolbar'
import { InfoBox } from 'src/components/common/ImportWizard'
import { useChartTheme } from './chartTheme'
import RankedBarList from './RankedBarList'
import { ChartSkeleton, EmptyState, ErrorState, capWithOther, formatNumber } from './common'

/**
 * entity_type -> what an admin calls it, and whether a null name is genuinely
 * a deletion. ad/sponsor/player_sponsor are looked up by primary key, so a miss
 * means the row is gone. `partners`' name column could not be confirmed from
 * the API's own source, so a miss there is NOT claimed to be a deletion.
 */
const TYPE_META = {
  ad: { label: 'Ad', badge: 'info', deletedClaim: true },
  sponsor: { label: 'Sponsor', badge: 'success', deletedClaim: true },
  player_sponsor: { label: 'Player sponsor', badge: 'warning', deletedClaim: true },
  partner: { label: 'Partner', badge: 'secondary', deletedClaim: false },
}

const displayName = (row) => {
  const meta = TYPE_META[row.entity_type] || { label: row.entity_type, deletedClaim: false }
  if (row.name) return row.name
  return meta.deletedClaim
    ? `Deleted ${meta.label.toLowerCase()} (#${row.table_id})`
    : `${meta.label} (#${row.table_id})`
}

const columns = [
  { key: 'entity_type', label: 'Type', filter: false, _style: { width: '14%' } },
  { key: 'name', label: 'Name', filter: false, _style: { minWidth: '220px' } },
  { key: 'clicks', label: 'Clicks', filter: false, _style: { width: '14%' } },
  { key: 'unique_users', label: 'People who clicked', filter: false, _style: { width: '18%' } },
]

const SEARCH_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'entity_type', label: 'Type' },
]

/**
 * Top ten by clicks, as a ranked list. Same reasoning as the screens list:
 * click counts are small integers and a canvas bar chart reads poorly at that
 * scale — especially with a single ad, where one lone bar fills the card.
 * Anything past ten folds into "Other" so the totals still add up.
 */
const TopClicksList = ({ rows, theme }) => {
  const items = useMemo(
    () =>
      capWithOther(
        (rows || []).map((r) => ({ ...r, label: displayName(r) })),
        10,
        ['clicks', 'unique_users'],
        'label',
      ).map((row) => ({
        key: row.__isOther ? '__other' : `${row.entity_type}-${row.table_id}`,
        label: row.label,
        sublabel: row.__isOther ? undefined : TYPE_META[row.entity_type]?.label,
        value: Number(row.clicks) || 0,
        people: Number(row.unique_users) || 0,
      })),
    [rows],
  )

  return (
    <RankedBarList
      rows={items}
      color={theme.series[0]}
      valueLabel="clicks"
      emptyLabel="No clicks in this period."
    />
  )
}

const AdsSponsorsTab = ({ data, loading, error, onRetry }) => {
  const theme = useChartTheme()
  const [searchField, setSearchField] = useState(SEARCH_FIELDS[0].key)
  const [search, setSearch] = useState('')

  const rows = useMemo(() => data?.rows || [], [data])
  const hasData = rows.length > 0

  const tableItems = useMemo(() => {
    const mapped = rows.map((row) => ({
      ...row,
      clicks: Number(row.clicks) || 0,
      unique_users: Number(row.unique_users) || 0,
      name: displayName(row),
    }))

    if (!search) return mapped

    const needle = search.toLowerCase()
    return mapped.filter((row) =>
      String(row[searchField] || '')
        .toLowerCase()
        .includes(needle),
    )
  }, [rows, search, searchField])

  return (
    <>
      <CCard className="mb-3">
        <CCardHeader>
          <strong>Promos, sponsors &amp; partners</strong>
          <div className="small text-medium-emphasis">Top 10 by number of clicks</div>
        </CCardHeader>
        <CCardBody>
          {loading && <ChartSkeleton height={300} />}
          {!loading && error && <ErrorState message={error} onRetry={onRetry} height={300} />}
          {!loading && !error && !hasData && (
            <EmptyState
              title="No promo, sponsor or partner clicks in this period"
              hint="These are outbound taps on promos, sponsor cards and partner links — try a wider date range."
              height={300}
            />
          )}
          {!loading && !error && hasData && <TopClicksList rows={rows} theme={theme} />}
        </CCardBody>
      </CCard>

      <CCard>
        <CCardHeader>
          <strong>All clicks</strong>
          <div className="small text-medium-emphasis">
            Every ad, sponsor and partner link tapped in this period
          </div>
        </CCardHeader>
        <CCardBody>
          <InfoBox color="info">
            These links send people to an external site (ad landers, sponsor pages, partner
            socials), so a click is the only thing this app can honestly measure — there is no view
            or impression count to compare it against.
          </InfoBox>

          {loading && <ChartSkeleton height={240} />}
          {!loading && error && <ErrorState message={error} onRetry={onRetry} height={240} />}
          {!loading && !error && !hasData && <EmptyState height={240} />}
          {!loading && !error && hasData && (
            <>
              <TableSearchToolbar
                fields={SEARCH_FIELDS}
                field={searchField}
                onFieldChange={(next) => {
                  setSearchField(next)
                  setSearch('')
                }}
                value={search}
                onSearch={setSearch}
                placeholder="e.g. a sponsor name"
              />
              <div className="ge-datatable ge-datatable--no-select">
                <CSmartTable
                  items={tableItems}
                  columns={columns}
                  columnSorter
                  itemsPerPage={15}
                  itemsPerPageSelect
                  pagination
                  loading={loading}
                  noItemsLabel="No clicks found"
                  sorterValue={{ column: 'clicks', state: 'desc' }}
                  tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
                  scopedColumns={{
                    entity_type: (item) => {
                      const meta = TYPE_META[item.entity_type] || {
                        label: item.entity_type,
                        badge: 'secondary',
                      }
                      return (
                        <td>
                          <CBadge color={meta.badge} shape="rounded-pill" className="px-2 py-1">
                            {meta.label}
                          </CBadge>
                        </td>
                      )
                    },
                    name: (item) => <td className="fw-semibold">{item.name}</td>,
                    clicks: (item) => <td className="fw-semibold">{formatNumber(item.clicks)}</td>,
                    unique_users: (item) => (
                      <td className="text-medium-emphasis">{formatNumber(item.unique_users)}</td>
                    ),
                  }}
                />
              </div>
            </>
          )}
        </CCardBody>
      </CCard>
    </>
  )
}

export default AdsSponsorsTab
